import {
    Autocomplete,
    Box,
    CircularProgress,
    FormControl,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Radio,
    RadioGroup,
    TextField,
    Typography
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../components/button/Button';
import { SrtBaseFieldConfig, SrtNestedRepeatableConfig, SrtRepeatableConfig } from '../types';
import { srtStyles } from '../styles';
import { isSrtFieldDisabled, isSrtFieldVisible, resolveSrtField } from '../utils/srtFields';

const isNestedRepeatableVisible = (nested: SrtNestedRepeatableConfig, row: Record<string, any>) =>
    !nested.visibleWhen ||
    String(row?.[nested.visibleWhen.fieldName] ?? '') === String(nested.visibleWhen.value);

const exceptNumericSymbols = ['e', 'E', '+', '-', '.'];

interface BaseFieldProps {
    field: SrtBaseFieldConfig;
    value: any;
    error?: string;
    onChange: (value: any) => void;
    onBlur: (value: any) => void;
    disabled?: boolean;
    loading?: boolean;
}

export const SrtBaseField = ({
    field,
    value,
    error,
    onChange,
    onBlur,
    disabled,
    loading
}: BaseFieldProps) => {
    const { t } = useTranslation('siniestrosRoam');
    const [draftValue, setDraftValue] = useState(value ?? '');

    useEffect(() => {
        setDraftValue(value ?? '');
    }, [value]);

    const sanitizeNumericValue = (nextValue: any) => {
        const stringValue = String(nextValue ?? '');

        if (stringValue === '') return '';

        const normalizedValue = field.numericOnly
            ? stringValue.replace(/\D/g, '')
            : stringValue.replace(/-/g, '');

        return normalizedValue;
    };

    const handleDraftChange = (nextValue: any) => {
        const isNumericInput = field.inputType === 'number' || field.numericOnly;
        const sanitizedValue = isNumericInput ? sanitizeNumericValue(nextValue) : nextValue;

        if (isNumericInput && field.maxLength && String(sanitizedValue).length > field.maxLength) {
            setDraftValue(String(sanitizedValue).slice(0, field.maxLength));
            return;
        }

        setDraftValue(sanitizedValue);

        if (field.commitOnChange) {
            onChange(sanitizedValue);
        }
    };

    const handleCommit = () => {
        onChange(draftValue);
        onBlur(draftValue);
    };

    const hasFixedLengthValidation = field.validations?.some(
        (validation) => validation.name === 'fixedLength' && typeof validation.param === 'number'
    );
    const hasCharacterCounter =
        (field.type === 'textarea' || hasFixedLengthValidation || field.showCharacterCounter) &&
        typeof field.maxLength === 'number';
    const characterCount = field.characterCount ?? String(draftValue ?? '').length;
    const characterCounterMax = field.characterCounterMax ?? field.maxLength;
    const helperContent =
        error || field.helperText || hasCharacterCounter ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {error && (
                    <Typography variant="caption" color="error">
                        {error}
                    </Typography>
                )}
                {field.helperText && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {field.helperText}
                    </Typography>
                )}
                {hasCharacterCounter && (
                    <Typography
                        variant="caption"
                        sx={{
                            color: error ? 'error.main' : 'text.secondary',
                            alignSelf: 'flex-end'
                        }}
                    >
                        {characterCount}/{characterCounterMax}
                    </Typography>
                )}
            </Box>
        ) : undefined;

    // Un campo readOnlyPlain (ej. patologia trazadora autocompletada) no debe verse
    // "deshabilitado" (texto gris) aunque no sea editable: solo bloquea el tipeo nativo.
    const isReadOnlyNative = disabled || field.readOnly || loading;
    const isDisabled = disabled || (field.readOnly && !field.readOnlyPlain) || loading;
    const loadingAdornment = loading ? (
        <InputAdornment position="end">
            <CircularProgress size={16} />
        </InputAdornment>
    ) : undefined;
    const commonTextFieldProps = {
        fullWidth: true,
        size: 'small' as const,
        label: field.label,
        disabled: isDisabled,
        required: field.required,
        error: !!error,
        helperText: helperContent,
        FormHelperTextProps: { component: 'div' as const },
        sx: srtStyles.textField
    };

    if (field.type === 'select') {
        const selectableOptions = (field.options || []).filter((option) => option.value !== '');
        const selectedOption =
            selectableOptions.find((option) => String(option.value) === String(value ?? '')) ||
            null;

        return (
            <Autocomplete
                options={selectableOptions}
                value={selectedOption}
                disabled={isDisabled}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, selectedValue) =>
                    String(option.value) === String(selectedValue.value)
                }
                onChange={(_event, nextOption) => onChange(nextOption?.value ?? '')}
                onBlur={() => onBlur(value)}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        {...commonTextFieldProps}
                        placeholder={
                            field.placeholder || String(t('detail.srtForm.options.select'))
                        }
                    />
                )}
            />
        );
    }

    if (field.type === 'textarea') {
        return (
            <TextField
                {...commonTextFieldProps}
                value={draftValue}
                multiline
                minRows={3}
                placeholder={field.placeholder}
                onChange={(event) => handleDraftChange(event.target.value)}
                onBlur={handleCommit}
                inputProps={{ maxLength: field.maxLength }}
            />
        );
    }

    if (field.type === 'radio') {
        return (
            <FormControl sx={srtStyles.radioControl} error={!!error}>
                <Typography variant="body1" sx={srtStyles.radioLabel}>
                    {field.label}
                </Typography>
                <RadioGroup
                    row
                    value={value ?? ''}
                    onChange={(event) => onChange(event.target.value)}
                    onBlur={() => onBlur(value)}
                >
                    {(field.options || []).map((option) => (
                        <FormControlLabel
                            key={String(option.value)}
                            value={String(option.value)}
                            disabled={isDisabled}
                            control={
                                <Radio
                                    size="small"
                                    onClick={() => {
                                        if (
                                            field.allowDeselect !== false &&
                                            String(value) === String(option.value)
                                        ) {
                                            onChange('');
                                        }
                                    }}
                                />
                            }
                            label={option.label}
                        />
                    ))}
                </RadioGroup>
                {!!error && (
                    <Typography variant="caption" color="error">
                        {error}
                    </Typography>
                )}
            </FormControl>
        );
    }

    if (field.type === 'file') {
        return (
            <TextField
                {...commonTextFieldProps}
                type="file"
                value={undefined}
                InputLabelProps={{ shrink: true }}
                onBlur={() => onBlur(value)}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    onChange(event.target.files?.[0] || null)
                }
            />
        );
    }

    return (
        <TextField
            {...commonTextFieldProps}
            value={draftValue}
            type={
                field.type === 'date' || field.type === 'time'
                    ? field.type
                    : field.inputType || 'text'
            }
            placeholder={field.placeholder}
            InputLabelProps={
                field.type === 'date' || field.type === 'time' || field.numericOnly
                    ? { shrink: true }
                    : undefined
            }
            onChange={(event) => handleDraftChange(event.target.value)}
            onBlur={handleCommit}
            onKeyDown={(event) => {
                if (field.inputType === 'number' && exceptNumericSymbols.includes(event.key)) {
                    event.preventDefault();
                }
            }}
            InputProps={{ endAdornment: loadingAdornment }}
            inputProps={{
                readOnly: isReadOnlyNative,
                maxLength: field.inputType === 'text' ? field.maxLength : undefined,
                inputMode: field.numericOnly ? 'numeric' : undefined,
                pattern: field.numericOnly ? '[0-9]*' : undefined,
                onInput: (event: ChangeEvent<HTMLInputElement>) => {
                    if (
                        field.inputType === 'number' &&
                        field.maxLength &&
                        event.target.value.length > field.maxLength
                    ) {
                        event.target.value = event.target.value.slice(0, field.maxLength);
                        handleDraftChange(event.target.value);
                    }
                }
            }}
        />
    );
};

interface FieldRendererProps {
    field: SrtBaseFieldConfig;
    value: any;
    error?: string;
    onChange: (fieldName: string, value: any) => void;
    onBlur: (field: SrtBaseFieldConfig, value: any) => void;
    disabled?: boolean;
}

export const SrtFieldRenderer = ({
    field,
    value,
    error,
    onChange,
    onBlur,
    disabled
}: FieldRendererProps) => (
    <Box sx={srtStyles.fieldGridColumn(field.gridColumns)}>
        <SrtBaseField
            field={field}
            value={value}
            error={error}
            onChange={(nextValue) => onChange(field.name, nextValue)}
            onBlur={(nextValue) => onBlur(field, nextValue)}
            disabled={disabled}
        />
    </Box>
);

interface RepeatableRendererProps {
    field: SrtRepeatableConfig;
    rows: Record<string, any>[];
    errors: Record<string, string>;
    onChange: (groupName: string, rowIndex: number, fieldName: string, value: any) => void;
    onBlur: (field: SrtBaseFieldConfig, value: any, groupName: string, rowIndex: number) => void;
    onAdd: (field: SrtRepeatableConfig) => void;
    onRemove: (groupName: string, rowIndex: number) => void;
    canAdd?: boolean;
    disabled?: boolean;
    isCellLoading?: (rowIndex: number, fieldName: string) => boolean;
    // Solo aplican cuando field.nestedRepeatable existe (hoy, "medidas" dentro de cada causa).
    onNestedChange?: (rowIndex: number, nestedIndex: number, fieldName: string, value: any) => void;
    onNestedBlur?: (
        field: SrtBaseFieldConfig,
        value: any,
        rowIndex: number,
        nestedIndex: number
    ) => void;
    onNestedAdd?: (rowIndex: number) => void;
    onNestedRemove?: (rowIndex: number, nestedIndex: number) => void;
    nestedMaxRows?: number;
}

export const SrtRepeatableRenderer = ({
    field,
    rows,
    errors,
    onChange,
    onBlur,
    onAdd,
    onRemove,
    canAdd = true,
    disabled,
    isCellLoading,
    onNestedChange,
    onNestedBlur,
    onNestedAdd,
    onNestedRemove,
    nestedMaxRows
}: RepeatableRendererProps) => {
    const { t } = useTranslation('siniestrosRoam');

    return (
        <Box sx={srtStyles.repeatableWrapper}>
            {(rows || []).map((row, rowIndex) => (
                <Box key={`${field.name}-${rowIndex}`} sx={srtStyles.repeatableRow}>
                    {field.fields
                        .filter((childField) => isSrtFieldVisible(childField, row))
                        .map((childField) => {
                            const resolvedField = resolveSrtField(childField, row);

                            return (
                                <Box
                                    key={resolvedField.name}
                                    sx={srtStyles.fieldGridColumn(resolvedField.gridColumns)}
                                >
                                    <SrtBaseField
                                        field={resolvedField}
                                        value={row?.[resolvedField.name]}
                                        error={
                                            errors[
                                                `${field.name}.${rowIndex}.${resolvedField.name}`
                                            ]
                                        }
                                        onChange={(nextValue) =>
                                            onChange(
                                                field.name,
                                                rowIndex,
                                                resolvedField.name,
                                                nextValue
                                            )
                                        }
                                        onBlur={(nextValue) =>
                                            onBlur(resolvedField, nextValue, field.name, rowIndex)
                                        }
                                        disabled={
                                            disabled || isSrtFieldDisabled(resolvedField, row)
                                        }
                                        loading={isCellLoading?.(rowIndex, resolvedField.name)}
                                    />
                                </Box>
                            );
                        })}
                    <IconButton
                        aria-label={String(t('detail.srtForm.actions.remove'))}
                        onClick={() => onRemove(field.name, rowIndex)}
                        sx={srtStyles.deleteButton}
                        disabled={disabled}
                    >
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>

                    {field.nestedRepeatable &&
                        isNestedRepeatableVisible(field.nestedRepeatable, row) && (
                            <Box sx={srtStyles.nestedRepeatableWrapper}>
                                <Box sx={srtStyles.nestedAddButtonWrapper}>
                                    <Button
                                        type="outlined"
                                        onClick={() => onNestedAdd?.(rowIndex)}
                                        style={srtStyles.addButton as object}
                                        disabled={
                                            disabled ||
                                            (nestedMaxRows !== undefined &&
                                                (row?.[field.nestedRepeatable!.name] || [])
                                                    .length >= nestedMaxRows)
                                        }
                                    >
                                        {field.nestedRepeatable.addLabel}
                                    </Button>
                                </Box>
                                {(row?.[field.nestedRepeatable.name] || []).map(
                                    (nestedRow: Record<string, any>, nestedIndex: number) => (
                                        <Box
                                            key={`${field.name}-${rowIndex}-${field.nestedRepeatable!.name}-${nestedIndex}`}
                                            sx={srtStyles.nestedRepeatableRow}
                                        >
                                            {field
                                                .nestedRepeatable!.fields.filter((childField) =>
                                                    isSrtFieldVisible(childField, nestedRow)
                                                )
                                                .map((childField) => {
                                                    const resolvedField = resolveSrtField(
                                                        childField,
                                                        nestedRow
                                                    );

                                                    return (
                                                        <Box
                                                            key={resolvedField.name}
                                                            sx={srtStyles.fieldGridColumn(
                                                                resolvedField.gridColumns
                                                            )}
                                                        >
                                                            <SrtBaseField
                                                                field={resolvedField}
                                                                value={
                                                                    nestedRow?.[resolvedField.name]
                                                                }
                                                                error={
                                                                    errors[
                                                                        `${field.name}.${rowIndex}.${field.nestedRepeatable!.name}.${nestedIndex}.${resolvedField.name}`
                                                                    ]
                                                                }
                                                                onChange={(nextValue) =>
                                                                    onNestedChange?.(
                                                                        rowIndex,
                                                                        nestedIndex,
                                                                        resolvedField.name,
                                                                        nextValue
                                                                    )
                                                                }
                                                                onBlur={(nextValue) =>
                                                                    onNestedBlur?.(
                                                                        resolvedField,
                                                                        nextValue,
                                                                        rowIndex,
                                                                        nestedIndex
                                                                    )
                                                                }
                                                                disabled={
                                                                    disabled ||
                                                                    isSrtFieldDisabled(
                                                                        resolvedField,
                                                                        nestedRow
                                                                    )
                                                                }
                                                            />
                                                        </Box>
                                                    );
                                                })}
                                            <IconButton
                                                aria-label={String(
                                                    t('detail.srtForm.actions.remove')
                                                )}
                                                onClick={() =>
                                                    onNestedRemove?.(rowIndex, nestedIndex)
                                                }
                                                sx={srtStyles.nestedDeleteButton}
                                                disabled={disabled}
                                            >
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    )
                                )}
                            </Box>
                        )}
                </Box>
            ))}
            <Box sx={srtStyles.addButtonWrapper}>
                <Button
                    type="outlined"
                    onClick={() => onAdd(field)}
                    style={srtStyles.addButton as object}
                    disabled={!canAdd || disabled}
                >
                    {field.addLabel}
                </Button>
            </Box>
        </Box>
    );
};
